import { Autocomplete, Box, Button, Link, Divider, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { type Application } from '../../../shared/types';
import { AdvisoryResults } from './AdvisoryResults';
import { useAdvisory, useApplications } from '../../hooks/useAdvisory';

export const Advisory = () => {
    const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
    const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
    const [advisoryId, setAdvisoryId] = useState<string | null>(null);

    const { data: applications = [], isLoading: appsLoading } = useApplications();
    const { data: advisories, isFetching, isFetched } = useAdvisory(advisoryId);

    const folders = [...new Set(applications.map(({ folder }) => folder))].sort();

    const filteredApplications = selectedFolder ? applications.filter(({ folder }) => folder === selectedFolder).sort((a, b) => a.name.localeCompare(b.name)) : applications.sort((a, b) => a.name.localeCompare(b.name));

    const handleFolderChange = (_: React.SyntheticEvent, value: string | null) => {
        setSelectedFolder(value);
        setSelectedApplication(null);
        setAdvisoryId(null);
    };

    const handleApplicationChange = (_: React.SyntheticEvent, value: Application | null) => {
        setSelectedApplication(value);
        setAdvisoryId(null);
    };

    const handleCalculate = () => {
        if (selectedApplication) {
            setAdvisoryId(selectedApplication._id.toString());
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h5">Herbicide Stewardship (per Manufacturer's Label, see below)</Typography>
                <Typography variant="h6">
                    Always consult the{' '}
                    <Link target="_blank" href="https://www.cdpr.ca.gov/stakeholder-notices-and-info/">
                        County Ag Commissioner's Office
                    </Link>{' '}
                    for additional local regulations.
                </Typography>
                <Typography>
                    The following data (contingency table below) are for planning purposes only and are to be used at your own risk. Forecast data is highly-provisional in nature and should be used accordingly. After selecting your spray
                    application and pressing “Calculate”, click “Show Details” to reveal all weather criteria exceedances. Very low wind speeds and relative humidity are, in most cases, marked as “Questionable” or “Unfavorable” Conditions
                    due to their negative impact on dispersion, droplet integrity and droplet size (see Manufacturer’s Label below). Proximity to sensitive crops takes precedence over any information provided herein.
                </Typography>
                <Typography>Always be sure to consult a site’s real-time observation before commencing a job as the grower and applicator share responsibility for the application.</Typography>
            </Box>
            <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Autocomplete
                    sx={{ minWidth: 250 }}
                    options={folders}
                    loading={appsLoading}
                    value={selectedFolder}
                    onChange={handleFolderChange}
                    renderOption={(props: object, folder: string) => (
                        <ListItem {...props}>
                            <ListItemText primary={folder} />
                        </ListItem>
                    )}
                    renderInput={(params) => <TextField {...params} label="Category" variant="outlined" size="small" />}
                />
                <Autocomplete
                    sx={{ minWidth: 300 }}
                    options={filteredApplications}
                    getOptionLabel={(option) => option.name}
                    loading={appsLoading}
                    value={selectedApplication}
                    onChange={handleApplicationChange}
                    renderOption={(props: object, app: Application) => (
                        <ListItem {...props}>
                            <ListItemText primary={app.name} secondary={app.folder} />
                        </ListItem>
                    )}
                    renderInput={(params) => <TextField {...params} label="Application" variant="outlined" size="small" />}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                />
                <Button variant="contained" onClick={handleCalculate} disabled={!selectedApplication || isFetching} loading={isFetching}>
                    Calculate
                </Button>
            </Box>
            <Divider />
            <Box sx={{ flex: 1, p: 2 }}>
                {isFetched && advisories && selectedApplication && <AdvisoryResults advisories={advisories} application={selectedApplication} />}
                {isFetched && advisories && advisories.length === 0 && <Typography color="text.secondary">No advisory data available for this application.</Typography>}
            </Box>
        </Box>
    );
};
